import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft,
  PhoneCall,
  Bot,
  PhoneOff,
  Clock,
  CheckCircle2,
  MessageCircle,
  ListTodo,
  Loader2,
  History,
} from 'lucide-react';
import { useDialerSessionDetail, computeSessionStats, formatDuration } from '@/hooks/useDialerAnalytics';
import { useTwilio } from '@/contexts/TwilioContext';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { DialerAttempt } from '@/hooks/usePowerDialer';

const outcomeLabel: Record<string, string> = {
  human: 'Humano',
  machine: 'Caixa postal',
  no_answer: 'Não atendeu',
  busy: 'Ocupado',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

const outcomeTone: Record<string, string> = {
  human: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  machine: 'bg-amber-50 text-amber-700 border-amber-200',
  no_answer: 'bg-muted text-muted-foreground',
  busy: 'bg-orange-50 text-orange-700 border-orange-200',
  failed: 'bg-destructive/10 text-destructive border-destructive/20',
};

function nameOf(a: DialerAttempt) {
  return [a.contact?.first_name, a.contact?.last_name].filter(Boolean).join(' ') || a.to_e164;
}

export default function DialerSessionResult() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useDialerSessionDetail(id);
  const { makeCall, isReady } = useTwilio();
  const { data: currentUser } = useCurrentAppUser();
  const [creatingFor, setCreatingFor] = React.useState<string | null>(null);

  const session = data?.session ?? null;
  const attempts = data?.attempts ?? [];
  const stats = computeSessionStats(session, attempts);

  const handleCall = async (a: DialerAttempt) => {
    if (!isReady) return toast.error('Telefone não está pronto. Aguarde a conexão.');
    try {
      await makeCall(a.to_e164, a.contact_id ?? undefined);
    } catch {
      toast.error('Não foi possível iniciar a ligação');
    }
  };

  const handleTask = async (a: DialerAttempt) => {
    setCreatingFor(a.id);
    try {
      const due = new Date();
      due.setHours(due.getHours() + 4);
      const { error } = await supabase.from('tasks').insert([
        {
          title: `Retornar ligação — ${nameOf(a)}`,
          description: `Tentativa do Power Dialer em ${new Date(a.created_at).toLocaleString('pt-BR')} para ${
            a.to_e164
          }. Resultado: ${outcomeLabel[a.outcome ?? ''] ?? a.outcome ?? 'sem resultado'}.`,
          status: 'pending',
          priority: 'medium',
          due_date: due.toISOString(),
          contact_id: a.contact_id ?? null,
          assigned_to: currentUser?.id ?? null,
          created_by: currentUser?.id ?? null,
        },
      ]);
      if (error) throw error;
      toast.success('Tarefa de retorno criada');
    } catch (e) {
      toast.error('Erro ao criar tarefa: ' + (e as Error).message);
    } finally {
      setCreatingFor(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando sessão...
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-muted-foreground">Sessão não encontrada.</p>
        <Button variant="outline" onClick={() => navigate('/outbound')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar para prospecção
        </Button>
      </div>
    );
  }

  const metrics = [
    { label: 'Discadas', value: stats.dialed, icon: PhoneCall, tone: 'text-foreground' },
    { label: 'Humanos', value: stats.human, icon: CheckCircle2, tone: 'text-emerald-600' },
    { label: 'Caixa postal', value: stats.machine, icon: Bot, tone: 'text-amber-600' },
    { label: 'Sem resposta', value: stats.noAnswer, icon: PhoneOff, tone: 'text-muted-foreground' },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/outbound')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Resultado da sessão</h1>
            <p className="text-sm text-muted-foreground">
              {session.started_at ? new Date(session.started_at).toLocaleString('pt-BR') : '—'}
              {session.batch_label ? ` · ${session.batch_label}` : ''}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/outbound/discador/sessoes">
            <History className="h-4 w-4 mr-2" /> Minhas sessões
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map((m) => (
          <Card key={m.label} className="rounded-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <m.icon className="h-3.5 w-3.5" /> {m.label}
              </div>
              <div className={`text-2xl font-semibold mt-1 ${m.tone}`}>{m.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Taxa de contato humano</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-600">{stats.humanRate.toFixed(0)}%</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Tempo médio até conexão
            </div>
            <div className="text-2xl font-semibold mt-1">{formatDuration(stats.avgTimeToConnect)}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Tempo em conversa</div>
            <div className="text-2xl font-semibold mt-1">{formatDuration(stats.talkSeconds)}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Duração da sessão</div>
            <div className="text-2xl font-semibold mt-1">{formatDuration(stats.durationSeconds)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Contatos discados ({attempts.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {attempts.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhuma tentativa registrada nesta sessão.</p>
          )}
          {attempts.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 flex-wrap"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {a.contact_id ? (
                    <Link className="hover:underline" to={`/contact-detail?id=${a.contact_id}`}>
                      {nameOf(a)}
                    </Link>
                  ) : (
                    nameOf(a)
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.to_e164} · {new Date(a.created_at).toLocaleTimeString('pt-BR')}
                  {a.duration_seconds ? ` · ${formatDuration(a.duration_seconds)}` : ''}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={`rounded-full text-xs ${outcomeTone[a.outcome ?? ''] ?? 'bg-muted text-muted-foreground'}`}
                >
                  {outcomeLabel[a.outcome ?? ''] ?? a.outcome ?? '—'}
                </Badge>
                <Button size="sm" variant="outline" className="h-8" onClick={() => handleCall(a)}>
                  <PhoneCall className="h-3.5 w-3.5 mr-1.5" /> Ligar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  disabled={creatingFor === a.id}
                  onClick={() => handleTask(a)}
                >
                  <ListTodo className="h-3.5 w-3.5 mr-1.5" /> Tarefa
                </Button>
                <Button size="sm" variant="outline" className="h-8" asChild>
                  <a
                    href={`https://wa.me/${a.to_e164.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
